import type { UnitReport } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { periodLabel } from '../lib/report-period.js';

/**
 * The unit report: readiness (checks, faults, off run), rostering (crews,
 * cover, ops support, UDO) and people. Used on Admin → Reports and on the
 * printable page; plain layout so it prints cleanly.
 */
export function UnitReportView({ r }: { r: UnitReport }) {
  const c = r.checks;
  const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');
  return (
    <div className="unit-report" style={{ display: 'flex', flexDirection: 'column', gap: 18, fontFamily: theme.font, color: theme.text }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{r.orgName}</div>
        <div style={{ fontSize: 14, color: theme.textMuted }}>
          {periodLabel(r.from, r.to)} · made {new Date(r.generatedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
        </div>
      </div>

      <Section title="Readiness">
        <Tiles items={[
          ['Checks on time', pct(c.onTime, c.scheduled), `${c.onTime} of ${c.scheduled} scheduled`, c.missed ? 'warn' : undefined],
          ['Checks missed', String(c.missed), c.late ? `${c.late} done late` : 'none late', c.missed ? 'danger' : undefined],
          ['Faults fixed', String(r.faults.fixed), r.faults.avgDaysToFix !== null ? `avg ${r.faults.avgDaysToFix} days to fix` : `${r.faults.opened} found`],
          ['Faults open', String(r.faults.openNow), `${r.faults.opened} found in the period`, r.faults.openNow ? 'warn' : undefined],
        ]} />
        {c.onDemand > 0 && <Note>Plus {c.onDemand} on-demand check{c.onDemand > 1 ? 's' : ''}.</Note>}
        <Table title="Scheduled checks by team" head={['Team', 'Due', 'On time', 'Late', 'Missed', 'Open']}
          rows={c.byTeam.map((t) => [t.name, t.scheduled, t.onTime, t.late, t.missed, t.open])} empty="No scheduled checks were due." />
        <Table title="By vehicle" head={['Vehicle', 'Due', 'On time', 'Late', 'Missed', 'Open']}
          rows={c.byAsset.map((t) => [t.name, t.scheduled, t.onTime, t.late, t.missed, t.open])} />
        <Table title="Oldest open faults" head={['Fault', 'Vehicle', 'Days open', 'Assigned to']}
          rows={r.faults.oldest.map((f) => [f.title, f.asset, f.days, f.assignedTo ?? '—'])} empty="No open faults." />
        <Table title="Off run" head={['Vehicle', 'Days', 'Why', 'Now']}
          rows={r.offRun.map((o) => [o.asset, o.days, o.reasons.join('; ') || '—', o.stillOff ? 'Off run' : 'Back on'])} empty="No vehicle was off run." />
      </Section>

      <Section title="Rostering">
        <Tiles items={[
          ['Crew shifts short', String(r.crews.short), `of ${r.crews.shifts} duty-crew shifts`, r.crews.short ? 'danger' : undefined],
          ['No truck driver', String(r.crews.noTruck), r.crews.noSupport ? `${r.crews.noSupport} without a support driver` : 'support drivers covered', r.crews.noTruck ? 'warn' : undefined],
          ['Cover filled', pct(r.cover.covered, r.cover.requests), `${r.cover.covered} of ${r.cover.requests} requests`],
          ['Hours uncovered', String(r.cover.hoursUncovered), `of ${r.cover.hoursAsked} asked`, r.cover.hoursUncovered ? 'warn' : undefined],
        ]} />
        <Note>
          Ops support: {r.ops.claimed} of {r.ops.shifts} shifts claimed{r.ops.open ? `, ${r.ops.open} left open` : ''}.
          {r.udoUnpicked ? ` ${r.udoUnpicked} UDO shift${r.udoUnpicked > 1 ? 's' : ''} had nobody picked.` : ''}
          {r.crews.swahsShort ? ` SWAHS short on ${r.crews.swahsShort} shift${r.crews.swahsShort > 1 ? 's' : ''}.` : ''}
        </Note>
        <Table title="Duty crew by team" head={['Team', 'Shifts', 'Short of 3', 'Any warning']}
          rows={r.crews.byTeam.map((t) => [t.name, t.shifts, t.short, t.warned])} empty="No duty-crew shifts yet." />
        <Table title="Who covered" head={['Member', 'Times', 'Hours']}
          rows={r.cover.topCoverers.map((p) => [p.name, p.times, p.hours])} empty="Nobody covered anyone." />
        <Table title="Ops support load" head={['Member', 'Weeknights', 'Weekend nights', 'Day shifts']}
          rows={r.ops.byMember.map((m) => [m.name, m.weeknight, m.weekendNight, m.day])} empty="No ops-support shifts were claimed." />
      </Section>

      <Section title="People">
        <Tiles items={[
          ['Members', String(r.people.members), `${r.people.withApp} use the app`],
          ['Active', String(r.people.active30d), 'opened the app in the last 30 days'],
          ['On leave', String(r.people.onLeave.length), 'at some point in the period'],
          ['Quals expiring', String(r.people.quals.length), `${r.people.quals.filter((q) => q.expired).length} already expired`, r.people.quals.length ? 'warn' : undefined],
        ]} />
        <Table title="On leave" head={['Member', 'From', 'Until']}
          rows={r.people.onLeave.map((l) => [l.name, day(l.from), l.until ? day(l.until) : 'further notice'])} />
        <Table title="Qualifications expired or expiring in 90 days" head={['Member', 'Qualification', 'Expires']}
          rows={r.people.quals.map((q) => [q.name, q.code, `${day(q.expires)}${q.expired ? ' (expired)' : ''}`])} />
      </Section>
      <div style={{ fontSize: 11, color: theme.textMuted }}>
        Shifts count once they’ve started. Crew skills use members’ current qualifications.
      </div>
    </div>
  );
}

const day = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12, breakInside: 'avoid-page' }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: 0.2, borderBottom: `2px solid ${theme.border}`, paddingBottom: 6 }}>{title}</h2>
      {children}
    </section>
  );
}

function Tiles({ items }: { items: [string, string, string, ('warn' | 'danger')?][] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
      {items.map(([label, value, sub, tone]) => (
        <div key={label} style={{
          padding: '10px 12px', borderRadius: 10, border: `1px solid ${theme.border}`,
          background: tone ? theme.status[tone].bg : theme.surface, color: tone ? theme.status[tone].fg : theme.text,
        }}>
          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2 }}>{label}</div>
          <div style={{ fontSize: 11, opacity: 0.8 }}>{sub}</div>
        </div>
      ))}
    </div>
  );
}

function Table({ title, head, rows, empty }: { title: string; head: string[]; rows: (string | number)[][]; empty?: string }) {
  if (!rows.length && !empty) return null;
  return (
    <div style={{ breakInside: 'avoid' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.textDim, marginBottom: 4 }}>{title}</div>
      {!rows.length ? <Note>{empty}</Note> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>{head.map((h, i) => (
                <th key={h} style={{ textAlign: i ? 'right' : 'left', padding: '5px 8px', borderBottom: `1px solid ${theme.border}`, color: theme.textMuted, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>{row.map((v, i) => (
                  <td key={i} style={{ textAlign: i && typeof v === 'number' ? 'right' : 'left', padding: '5px 8px', borderBottom: `1px solid ${theme.border}`, fontVariantNumeric: 'tabular-nums' }}>{v}</td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, color: theme.textMuted }}>{children}</div>;
}
